import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message, Modal, DatePicker, Input } from 'antd';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageSkeleton from '../components/Skeleton';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const SIZE_LABEL = { standard: '标准展位', premium: '精品展位', island: '岛台展位' };
const TIER_LABEL = {
  premium:   { label: '优质商家', color: '#9B59B6', bg: '#F5EEF8' },
  excellent: { label: '优秀商家', color: '#2980B9', bg: '#EBF5FB' },
  good:      { label: '良好商家', color: '#27AE60', bg: '#EAFAF1' },
  normal:    { label: '普通商家', color: '#95A5A6', bg: '#F2F3F4' },
};

export default function BoothDetail() {
  const { t } = useTranslation();
  const { id: exhId, boothId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [data, setData] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAppoint, setShowAppoint] = useState(false);
  const [appointTime, setAppointTime] = useState(null);
  const [appointMsg, setAppointMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    api.get(`/exhibitions/booth/${boothId}`)
      .then(r => {
        setData(r.data);
        // 加载商家信用和评价
        if (r.data?.merchant_id) {
          api.get(`/merchants/${r.data.merchant_id}/credit`)
            .then(cr => setCredit(cr.data))
            .catch(() => {});
        }
        // 记录展位浏览
        api.post(`/exhibitions/booth/${boothId}/view`).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [boothId]);

  const handleAppointment = async () => {
    if (!appointTime) return message.warning(t('common.required'));
    if (!isLoggedIn) return message.warning(t('redirect.loginRequired'));
    setSubmitting(true);
    try {
      await api.post('/exhibitions/appointments', {
        booth_id: boothId,
        appointment_time: appointTime.format('YYYY-MM-DD HH:mm:00'),
        message: appointMsg || null,
      });
      message.success(t('exhibition.appointmentSuccess'));
      setShowAppoint(false);
      setAppointTime(null);
      setAppointMsg('');
    } catch (err) {
      message.error(err.response?.data?.error || t('common.failed'));
    } finally { setSubmitting(false); }
  };

  const handleFavorite = async (type, itemId) => {
    if (!isLoggedIn) return message.warning(t('redirect.loginRequired'));
    try {
      await api.post('/exhibitions/favorites', {
        type,
        booth_id: type === 'booth' ? boothId : null,
        item_id: type === 'item' ? itemId : null,
        exhibition_id: exhId,
      });
      message.success(t('common.success'));
    } catch { message.info(t('common.success')); }
  };

  if (loading) return <PageSkeleton />;
  if (!data) return <div style={{ textAlign: 'center', padding: 80, color: MUTED }}>{t('common.noData')}</div>;

  const items = data.items || [];
  const photos = data.booth_photos || [];

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 140 : 80 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>
            展位 {data.booth_no}
          </span>
          <button onClick={() => handleFavorite('booth')} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
          }}>♡</button>
        </div>

        {/* Booth Info Card */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 14,
          background: 'linear-gradient(135deg, #2A1A0A, #3A2A1A)',
          padding: '24px', position: 'relative', overflow: 'hidden',
        }}>
          {data.merchant_logo && (
            <div style={{
              position: 'absolute', right: 20, top: 20,
              width: 56, height: 56, borderRadius: 12,
              background: `url(${data.merchant_logo}) center/cover`,
              border: '2px solid rgba(255,255,255,.1)',
            }} />
          )}
          <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            {data.merchant_name || data.booth_no}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginBottom: 12 }}>
            {data.exhibition_name} · {data.zone || ''} {data.booth_no} · {SIZE_LABEL[data.size_type] || '标准展位'}
          </div>
          {data.merchant_desc && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', lineHeight: 1.6, marginBottom: 12 }}>
              {data.merchant_desc}
            </div>
          )}
          {(data.merchant_categories || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {data.merchant_categories.map(c => (
                <span key={c} style={{
                  padding: '2px 10px', borderRadius: 10, fontSize: 11,
                  background: 'rgba(255,255,255,.1)', color: GOLD,
                }}>{c}</span>
              ))}
            </div>
          )}
          {/* Credit Score Bar */}
          {data.credit_score > 0 && (() => {
            const t = TIER_LABEL[data.credit_tier] || TIER_LABEL.normal;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{
                  padding: '2px 10px', borderRadius: 10, fontSize: 11,
                  background: t.bg, color: t.color, fontWeight: 600,
                }}>{t.label}</span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>
                  信用 {Number(data.credit_score).toFixed(0)}
                </span>
                {data.avg_rating > 0 && (
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>
                    {'★'.repeat(Math.round(Number(data.avg_rating)))} {Number(data.avg_rating).toFixed(1)}
                    <span style={{ fontSize: 10, opacity: .6 }}> ({data.review_count}评)</span>
                  </span>
                )}
                {data.merchant_since && (
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,.4)' }}>
                    入驻 {new Date(data.merchant_since).toLocaleDateString('zh-CN', { year: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Booth Photos */}
        {photos.length > 0 && (
          <div style={{
            display: 'flex', gap: 8, padding: '12px 16px',
            overflowX: 'auto', scrollbarWidth: 'none',
          }}>
            {photos.map((url, i) => (
              <div key={i} style={{
                width: 120, height: 90, borderRadius: 8, flexShrink: 0,
                background: `url(${url}) center/cover`,
              }} />
            ))}
          </div>
        )}

        {/* Contact & Live */}
        <div style={{
          margin: '0 16px', padding: '12px 16px',
          background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
          display: 'flex', gap: 16, flexWrap: 'wrap',
        }}>
          {data.contact_name && (
            <div style={{ fontSize: 13, color: MUTED }}>
              联系人: <span style={{ color: INK }}>{data.contact_name}</span>
            </div>
          )}
          {data.contact_phone && (
            <div style={{ fontSize: 13, color: MUTED }}>
              电话: <span style={{ color: INK }}>{data.contact_phone}</span>
            </div>
          )}
          {data.live_url && (
            <a href={data.live_url} target="_blank" rel="noopener noreferrer" style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 4,
              background: '#E74C3C', color: '#fff', textDecoration: 'none',
            }}>🔴 观看直播</a>
          )}
        </div>

        {/* Items */}
        <div style={{ margin: '16px 16px 0' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 12 }}>
            展品 ({items.length})
          </div>
          {items.length === 0 ? (
            <div style={{
              background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
              padding: '30px 16px', textAlign: 'center', color: MUTED, fontSize: 13,
            }}>{t('common.noData')}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {items.map(item => {
                const thumb = (item.photos || [])[0] || '';
                const price = item.exhibition_price || item.price_cny;
                return (
                  <button key={item.id} onClick={() => navigate(`/market/${item.id}`)} style={{
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    overflow: 'hidden', cursor: 'pointer', textAlign: 'left', padding: 0,
                  }}>
                    <div style={{
                      height: 130, position: 'relative',
                      background: thumb ? `url(${thumb}) center/cover` : '#F5F5F5',
                    }}>
                      {item.show_only === 1 && (
                        <span style={{
                          position: 'absolute', top: 6, left: 6,
                          fontSize: 9, padding: '2px 6px', borderRadius: 3,
                          background: 'rgba(0,0,0,.6)', color: '#fff',
                        }}>{t('exhibition.detail')}</span>
                      )}
                      {item.exhibition_price && item.exhibition_price < item.price_cny && (
                        <span style={{
                          position: 'absolute', top: 6, right: 6,
                          fontSize: 9, padding: '2px 6px', borderRadius: 3,
                          background: '#E74C3C', color: '#fff',
                        }}>{t('exhibition.title')}</span>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: INK,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{item.item_name}</div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{item.brand}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                          ¥{Number(price).toLocaleString()}
                        </span>
                        {item.exhibition_price && item.exhibition_price < item.price_cny && (
                          <span style={{ fontSize: 10, color: '#CCC', textDecoration: 'line-through' }}>
                            ¥{Number(item.price_cny).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Merchant Reviews */}
        {credit && credit.reviews && credit.reviews.length > 0 && (
          <div style={{ margin: '16px 16px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{t('landing.testimonials')}</span>
              <span style={{ fontSize: 12, color: MUTED }}>
                {credit.review_stats?.avg_rating > 0 && `${credit.review_stats.avg_rating}分`}
                {credit.review_stats?.total > 0 && ` · ${credit.review_stats.total}条`}
                {credit.review_stats?.good_rate > 0 && ` · ${credit.review_stats.good_rate}%好评`}
              </span>
            </div>
            {credit.reviews.slice(0, 5).map(r => (
              <div key={r.id} style={{
                background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                padding: '12px 16px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: INK }}>{r.customer_name || '匿名用户'}</span>
                  <span style={{ fontSize: 11, color: '#F39C12' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                </div>
                {r.content && <div style={{ fontSize: 13, color: INK, lineHeight: 1.6 }}>{r.content}</div>}
                {(r.tags || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {r.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: 10, padding: '1px 8px', borderRadius: 4,
                        background: `${TEAL}10`, color: TEAL,
                      }}>{tag}</span>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: MUTED, marginTop: 6 }}>
                  {new Date(r.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Action Bar */}
        <div style={{
          position: 'fixed', bottom: isMobile ? 56 : 0, left: 0, right: 0,
          background: '#fff', borderTop: `1px solid ${BD}`,
          padding: '12px 16px', display: 'flex', gap: 10,
          justifyContent: 'center', zIndex: 101,
        }}>
          <div style={{ maxWidth: 700, width: '100%', display: 'flex', gap: 10 }}>
            <button onClick={() => handleFavorite('booth')} style={{
              flex: 1, padding: '12px 0', borderRadius: 8,
              border: `1px solid ${BD}`, background: '#fff',
              fontSize: 14, color: MUTED, cursor: 'pointer',
            }}>♡ {t('browse.addWatchlist')}</button>
            <button onClick={() => {
              if (!isLoggedIn) { message.warning('请先登录'); return; }
              setShowAppoint(true);
            }} style={{
              flex: 2, padding: '12px 0', borderRadius: 8,
              border: 'none', background: TEAL, color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>{t('exhibition.appointment')}</button>
          </div>
        </div>

        {/* Appointment Modal */}
        <Modal
          open={showAppoint}
          onCancel={() => setShowAppoint(false)}
          onOk={handleAppointment}
          confirmLoading={submitting}
          title={t('exhibition.appointment')}
          okText={t('common.confirm')}
          cancelText={t('common.cancel')}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: INK, marginBottom: 6 }}>
              展位: {data.booth_no} — {data.merchant_name || ''}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('exhibition.time')} *</label>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={appointTime}
              onChange={setAppointTime}
              style={{ width: '100%' }}
              placeholder={t('exhibition.time')}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('common.optional')}</label>
            <Input.TextArea
              value={appointMsg}
              onChange={e => setAppointMsg(e.target.value)}
              placeholder={t('common.optional')}
              rows={3}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}
